'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { calcMaquininha, calcMarketplace } from '@/lib/taxas/calc'
import {
  MAQUININHAS,
  MARKETPLACES,
  getMaquininha,
  getMarketplace,
  TAXAS_DISCLAIMER,
  type ModalidadePagamento,
} from '@/lib/taxas/rates'
import {
  AvisoBox,
  brl,
  DetailRow,
  LeadCapture,
  SalvarCalculoCta,
  Eyebrow,
  MobileResultBar,
  MoneyField,
  parseValor,
  ResultCard,
  RowStrong,
  SoftExportBar,
  useAnimatedNumber,
  useCalcTelemetry,
} from '@/components/calc'
import type { CartaoPayload } from '@/lib/export/types'
import { cn } from '@/lib/utils'

type Canal = 'maquininha' | 'marketplace'

const MODALIDADES: { id: ModalidadePagamento; label: string }[] = [
  { id: 'pix', label: 'Pix' },
  { id: 'debito', label: 'Débito' },
  { id: 'credito_avista', label: 'Crédito à vista' },
  { id: 'credito_parcelado', label: 'Crédito parcelado' },
]

const selectCls =
  'h-[46px] w-full border border-line bg-surface px-3 text-sm text-ink outline-none focus:ring-1 focus:ring-inset focus:ring-brand'

export function QuantoSobraClient() {
  const [valor, setValor] = useState('')
  // Pré-preenche com o valor da venda vindo do console da home (?valor=).
  useEffect(() => {
    const v = new URLSearchParams(window.location.search).get('valor')
    if (v) setValor(v)
  }, [])
  const [canal, setCanal] = useState<Canal>('maquininha')
  const [maquininhaId, setMaquininhaId] = useState('mercadopago')
  const [modalidade, setModalidade] = useState<ModalidadePagamento>('credito_avista')
  const [parcelas, setParcelas] = useState(2)
  const [marketplaceId, setMarketplaceId] = useState('ifood_proprio')

  const r = useMemo(() => {
    const v = parseValor(valor)
    if (canal === 'maquininha') {
      const m = getMaquininha(maquininhaId) ?? MAQUININHAS[0]
      return calcMaquininha({ maquininha: m, valor: v, modalidade, parcelas })
    }
    const mp = getMarketplace(marketplaceId) ?? MARKETPLACES[0]
    return calcMarketplace({ marketplace: mp, valor: v })
  }, [valor, canal, maquininhaId, modalidade, parcelas, marketplaceId])

  const liquidoAnim = useAnimatedNumber(r.liquido)

  /** Guarda da barra mobile: sem isto ela nascia fixa no rodapé mostrando R$ 0,00. */
  const temResultado = parseValor(valor) > 0
  const isParcelado = canal === 'maquininha' && modalidade === 'credito_parcelado'

  useCalcTelemetry(
    'quanto_sobra',
    parseValor(valor) > 0,
    `Sobra ${brl(r.liquido)} de ${brl(r.bruto)}`,
  )

  const buildCartao = (): CartaoPayload => ({
    nome: 'quanto-sobra-de-verdade',
    heroLabel: 'Sobra pra você',
    heroValue: brl(r.liquido),
    tone: 'brand',
    linhas: [
      { label: 'Você vendeu por', value: brl(r.bruto) },
      {
        label: `Taxa (${r.taxaPct.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%)`,
        value: `− ${brl(r.taxaReais)}`,
      },
      { label: 'Sobra limpo', value: brl(r.liquido), strong: true, tone: 'brand' },
    ],
  })

  const recebimentoTexto = (() => {
    const rec = r.recebimento
    if (rec.tipo === 'na_hora') return 'Cai na hora (até 1 dia útil)'
    if (rec.tipo === 'parcelado')
      return `${rec.parcelas}x de ${brl(rec.valorParcela)} — última em ${rec.diasUltima} dias`
    return `Cai em ${rec.diasPrimeira} ${rec.diasPrimeira === 1 ? 'dia' : 'dias'}`
  })()

  const cardRef = useRef<HTMLElement>(null)

  return (
    <div className="grid grid-cols-1 gap-[18px] pb-24 lg:grid-cols-2 lg:items-start lg:pb-0">
      {/* ── INPUTS ── */}
      <section className="rounded-2xl border border-line bg-surface p-6 shadow-soft">
        <MoneyField
          label="Qual o valor da venda?"
          value={valor}
          onChange={setValor}
          hint="O preço cheio que o cliente paga."
        />

        <div className="mb-2 text-sm text-ink">Como você recebe?</div>
        <div className="mb-4 grid grid-cols-2 gap-2">
          <CanalTab active={canal === 'maquininha'} onClick={() => setCanal('maquininha')}>
            Maquininha
          </CanalTab>
          <CanalTab active={canal === 'marketplace'} onClick={() => setCanal('marketplace')}>
            App / Marketplace
          </CanalTab>
        </div>

        {canal === 'maquininha' ? (
          <>
            <label className="mb-2 block text-sm text-ink">Sua maquininha</label>
            <select className={cn(selectCls, 'mb-4')} value={maquininhaId} onChange={(e) => setMaquininhaId(e.target.value)}>
              {MAQUININHAS.map((m) => (
                <option key={m.id} value={m.id}>{m.nome}</option>
              ))}
            </select>

            <label className="mb-2 block text-sm text-ink">Forma de pagamento</label>
            <select
              className={cn(selectCls, isParcelado ? 'mb-3' : 'mb-0')}
              value={modalidade}
              onChange={(e) => setModalidade(e.target.value as ModalidadePagamento)}
            >
              {MODALIDADES.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>

            {isParcelado && (
              <>
                <label className="mb-2 block text-sm text-ink">Em quantas vezes?</label>
                <select className={selectCls} value={parcelas} onChange={(e) => setParcelas(Number(e.target.value))}>
                  {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => (
                    <option key={n} value={n}>{n}x</option>
                  ))}
                </select>
              </>
            )}
          </>
        ) : (
          <>
            <label className="mb-2 block text-sm text-ink">Onde você vende</label>
            <select className={selectCls} value={marketplaceId} onChange={(e) => setMarketplaceId(e.target.value)}>
              {MARKETPLACES.map((m) => (
                <option key={m.id} value={m.id}>{m.nome}</option>
              ))}
            </select>
          </>
        )}

        <p className="mt-4 text-[11.5px] leading-snug text-ink-faint">{TAXAS_DISCLAIMER}</p>
      </section>

      {/* ── RESULTADO ── */}
      <section ref={cardRef} className="flex flex-col rounded-2xl border border-line bg-bg-alt p-[22px] shadow-soft">
        <Eyebrow icon="wallet" tone="muted" className="mb-3">
          Quanto sobra de verdade
        </Eyebrow>

        <ResultCard heroLabel="Sobra pra você" heroValue={brl(liquidoAnim)}>
          <DetailRow label="Você vendeu por" value={brl(r.bruto)} />
          <DetailRow label={`Taxa (${r.taxaPct.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%)`} value={`− ${brl(r.taxaReais)}`} />
          <RowStrong label="Sobra limpo" value={brl(r.liquido)} tone="brand" />
          <p className="mt-1.5 font-mono text-[11.5px] text-ink-faint">{recebimentoTexto}</p>
        </ResultCard>

        {r.antecipacao && (
          <AvisoBox tone="amber" icon="alert" className="mt-4">
            Pra receber na hora em vez de esperar, a antecipação custa{' '}
            <strong>{brl(r.antecipacao.custoReais)}</strong> ({r.antecipacao.custoPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%) —
            sobrariam {brl(r.antecipacao.liquido)}. É juro disfarçado; vale só se você precisa do dinheiro agora.
          </AvisoBox>
        )}

        <SoftExportBar buildCartao={buildCartao} />
        <LeadCapture show={parseValor(valor) > 0} source="quanto-sobra-de-verdade" alertType="das" />
        <SalvarCalculoCta
          show={parseValor(valor) > 0}
          source="quanto-sobra-de-verdade"
          hook="Isso é o que sobra. No Premium você vê o mês e o comprovante limpo."
        />
      </section>

      {temResultado && <MobileResultBar payload={buildCartao()} cardRef={cardRef} />}
    </div>
  )
}

function CanalTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'border px-3 py-2.5 text-[13px] font-medium',
        active ? 'border-brand bg-brand-tint text-brand' : 'border-line bg-surface text-ink-mute hover:border-brand'
      )}
    >
      {children}
    </button>
  )
}
